// Authentication service to handle Google OAuth
// import { config } from '../config';

interface UserData {
  id: string;
  name: string;
  email: string;
  imageUrl?: string;
}

interface AuthResponse {
  success: boolean;
  userData?: UserData;
  token?: string;
  error?: string;
}

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT payload', error);
    return null;
  }
};

const TOKEN_KEY = 'authToken';
const USER_DATA_KEY = 'userData';
const LOGOUT_REASON_KEY = 'logoutReason';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const NO_ACTIVE_USER_MESSAGE = 'No active user found with the given email id.';

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

const readApiErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    if (data && typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
  } catch (error) {
    console.error('Failed to parse auth validation error', error);
  }
  return response.statusText || NO_ACTIVE_USER_MESSAGE;
};

export const authService = {
  async authenticateWithGoogle(token: string): Promise<AuthResponse> {
    try {
      const payload = decodeJwtPayload(token);

      if (!payload || !payload.email) {
        throw new Error('Invalid Google credential');
      }

      const userData: UserData = {
        id: payload.sub || payload.email,
        name: payload.name || payload.email.split('@')[0],
        email: payload.email,
        imageUrl: payload.picture
      };

      const response: AuthResponse = {
        success: true,
        userData,
        token
      };

      await authService.validateUserAccess(token);
      authService.persistSession(token, userData);
      
      return response;
    } catch (error) {
      console.error('Authentication error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  },

  async validateUserAccess(token: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/users/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await readApiErrorMessage(response));
    }
  },

  async validateStoredSession(): Promise<UserData | null> {
    const token = authService.getToken();
    const userData = authService.getUserData();
    if (!token || !userData) {
      return null;
    }
    await authService.validateUserAccess(token);
    return userData;
  },

  persistSession(token: string, userData: UserData) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  },

  clearStoredAuth,
  
  logout() {
    clearStoredAuth();
    localStorage.removeItem(LOGOUT_REASON_KEY);
    window.location.reload(); // Reload to reset the application state
  },

  logoutWithReason(reason: string) {
    localStorage.setItem(LOGOUT_REASON_KEY, reason);
    authService.logout();
  },
  
  isAuthenticated() {
    return !!localStorage.getItem(TOKEN_KEY);
  },
  
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },
  
  getUserData(): UserData | null {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  consumeLogoutReason(): string | null {
    const reason = localStorage.getItem(LOGOUT_REASON_KEY);
    if (reason) {
      localStorage.removeItem(LOGOUT_REASON_KEY);
      return reason;
    }
    return null;
  }
};
